// @fileoverview : 영업관리 > 수주현황

import { mutatePurchaseOrders } from "@/api/purchaseOrder/usePurchaseOrder";
import { users } from "@/api/users/useUsersQuery";
import { useModal } from "@/features/modal/ModalStackManager";
import { ProductionPlanCreateForm } from "@/features/productionPlan/form/CreateForm";
import { PurchaseOrderCreateItemsForm } from "@/features/purchaseOrder/form/CreateItemsForm";
import { PurchaseOrderOutgoingForm } from "@/features/purchaseOrder/purchaseOrderOutgoingForm";
import { PurchaseOrderTableHead } from "@/features/purchaseOrder/PurchaseOrderTableHead";
import { PurchaseOrderTableInRowBody } from "@/features/purchaseOrder/PurchaseOrderTableInRowBody";
import { PurchaseOrderTableInRowHead } from "@/features/purchaseOrder/PurchaseOrderTableInRowHead";
import { PurchaseOrderTableRow } from "@/features/purchaseOrder/PurchaseOrderTableRow";
import { useStandardLayout } from "@/features/standardLayout/Context";
import { Filter } from "@/features/standardLayout/Filter";
import { PaginationBox } from "@/features/standardLayout/PaginationBox";
import { SearchBox } from "@/features/standardLayout/SearchBox";
import { Sorter } from "@/features/standardLayout/Sorter";
import {
  flexGrowable,
  flexStable,
  StandardLayoutTableWrapper,
} from "@/features/standardLayout/Styles";
import customAlert from "@/features/ui/alert/Alert";
import { ConfirmForm } from "@/features/ui/confirm";
import { handleErrorResponse } from "@/utils/errorMessage";
import { translateWithPartnerType } from "@/utils/partnerType";
import { Avatar, Button, Flex, Table, Text, Tooltip } from "@mantine/core";
import {
  PartnersGet200ResponseRowsInnerTypeEnum,
  PurchaseOrdersGet200Response,
  PurchaseOrdersGet200ResponseRowsInnerPurchaseOrderItemsInner,
  ScmApiPurchaseOrdersPurchaseOrderIdDeleteRequest,
} from "@sizlcorp/sizl-api-document/dist/models";
import { IconFilterPlus, IconPlus, IconRecycle, IconTrash } from "@tabler/icons-react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

interface PurchaseOrderPageProps {
  children?: React.ReactNode;
}
export const PurchaseOrderPage = ({ children }: PurchaseOrderPageProps) => {
  const { t } = useTranslation();
  const { data: userInfo } = useQuery(users.who({}));
  const userCode = userInfo?.data?.code;
  const partnerType = userInfo?.data?.partner?.type;

  const { openModal, closeModal } = useModal();
  const { query, setQuery, selectedRows, refetch, resetQueryStrings, data } = useStandardLayout();

  const [checkedMap, setCheckedMap] = useState<Map<number, Set<number>>>(new Map());

  useEffect(() => {
    if (query.length === 0) {
      // query가 비어 있을 때만 실행
      setQuery([{ direction: { $eq: "INBOUND" } }]);
    }

    if (userInfo?.data?.externalUserType) {
      setQuery([{ partnerCode: { $eq: userInfo?.data?.partner?.code } }]);
    }
  }, [query]); // 의존성 배열 유지

  const { mutate: deleteMutate } = useMutation(
    (params: ScmApiPurchaseOrdersPurchaseOrderIdDeleteRequest) =>
      mutatePurchaseOrders
        .delete(params)
        .mutationFn(params as ScmApiPurchaseOrdersPurchaseOrderIdDeleteRequest | any),
    {
      onSuccess: () => {
        refetch();
        customAlert(
          t("message.deleteSuccess", {
            item: translateWithPartnerType(t, "purchaseOrder.purchaseOrder", partnerType),
          }),
          t("common.deleteSuccess"),
          "green",
        );
      },
      onError: (error) => {
        customAlert(
          handleErrorResponse({
            error,
            errorContext: `${translateWithPartnerType(t, "purchaseOrder.purchaseOrder", partnerType)}${translateWithPartnerType(t, "purchaseOrder.id", partnerType)}`,
          }),
          t("common.deleteFail", {
            item: translateWithPartnerType(t, "purchaseOrder.purchaseOrder", partnerType),
          }),
          "red",
        );
      },
    },
  );

  const handleDelete = () => {
    if ([...selectedRows].length === 0) {
      customAlert(
        t("message.deleteSelect", {
          item: translateWithPartnerType(t, "purchaseOrder.purchaseOrder", partnerType),
        }),
        t("common.deleteFail", {
          item: translateWithPartnerType(t, "purchaseOrder.purchaseOrder", partnerType),
        }),
        "red",
      );
      return;
    }

    openModal(
      <ConfirmForm
        message={t("message.rowDelete")}
        yesCallback={() => {
          [...selectedRows].forEach((row) => {
            deleteMutate({ purchaseOrderId: JSON.parse(row).id });
          });

          selectedRows.clear();
        }}
        noCallback={() => {}}
        commonCallback={() => closeModal(null)}
      />,
      null,
      `${t("common.delete", { item: translateWithPartnerType(t, "purchaseOrder.purchaseOrder", partnerType) })}`,
    );
  };

  return (
    <Flex w="100%" h="100%" gap="xs" direction="column">
      <Flex p="sm" justify="space-between" rowGap="md">
        <Flex gap="sm">
          {partnerType === PartnersGet200ResponseRowsInnerTypeEnum.SALES ? null : (
            <Button
              leftIcon={<IconPlus />}
              onClick={() =>
                openModal(
                  <PurchaseOrderCreateItemsForm />,
                  null,
                  t("common.additional", {
                    item: translateWithPartnerType(t, "purchaseOrder.both", partnerType),
                  }), // 수주 발주 추가
                  true,
                )
              }
            >
              {t("common.additional", {
                item: translateWithPartnerType(t, "purchaseOrder.both", partnerType),
              })}
            </Button>
          )}
          <Button
            leftIcon={<IconPlus />}
            color="orange"
            disabled={
              checkedMap.size == 0 ||
              (() => {
                const selectedItemIds = Array.from(checkedMap.values()).flatMap((set) =>
                  Array.from(set),
                );
                return (data as AxiosResponse<PurchaseOrdersGet200Response, any>)?.data?.rows
                  ?.flatMap((row) => row.purchaseOrderItems)
                  ?.filter((item) => selectedItemIds.includes(item?.id ?? 0))
                  ?.some((item) => item?.status === "DELIVERED");
              })()
            }
            onClick={() =>
              openModal(
                <PurchaseOrderOutgoingForm
                  purchaseOrder={() => {
                    const selectedItemIds = Array.from(checkedMap.values()).flatMap((set) =>
                      Array.from(set),
                    );

                    const matchedItems =
                      (
                        data as AxiosResponse<PurchaseOrdersGet200Response, any>
                      )?.data?.rows?.flatMap((row) =>
                        row.purchaseOrderItems?.filter((item) =>
                          selectedItemIds.includes(item.id ?? 0),
                        ),
                      ) ?? [];

                    return matchedItems;
                  }}
                />,
                null,
                t("delivery.delivery"),
              )
            }
          >
            {t("delivery.action")}
          </Button>
          <Button
            leftIcon={<IconPlus />}
            color="indigo"
            disabled={Array.from(checkedMap.values()).reduce((acc, set) => acc + set.size, 0) !== 1}
            onClick={() => {
              const selectedItemIds = Array.from(checkedMap.values()).flatMap((set) =>
                Array.from(set),
              );
              const matchedItems =
                (data as AxiosResponse<PurchaseOrdersGet200Response, any>)?.data?.rows?.flatMap(
                  (row) =>
                    row.purchaseOrderItems?.filter((item) =>
                      selectedItemIds.includes(item.id ?? 0),
                    ),
                ) ?? [];

              openModal(
                <ProductionPlanCreateForm
                  formatterProps={
                    matchedItems &&
                    (matchedItems[0] as PurchaseOrdersGet200ResponseRowsInnerPurchaseOrderItemsInner)
                  }
                />,
                null,
                t("common.create", { item: t("productionPlan.productionPlan") }),
                true,
              );
            }}
          >
            {t("common.create", { item: t("productionPlan.productionPlan") })}
          </Button>
        </Flex>
        <Flex gap="sm">
          <Button rightIcon={<IconRecycle />} color="teal" onClick={() => resetQueryStrings()}>
            {t("common.reset")}
          </Button>
          <Button rightIcon={<IconTrash />} color="red" onClick={() => handleDelete()}>
            {t("common.rowDelete")}
          </Button>
        </Flex>
      </Flex>
      {
        <Flex px="sm" direction="column" gap="xs">
          <Text fz="xl" fw={600}>
            {t("common.list", {
              item: translateWithPartnerType(t, "purchaseOrder.purchaseOrder", partnerType),
            })}
          </Text>
          <Text fz="xs">
            {t("common.subTitle", {
              item: translateWithPartnerType(t, "purchaseOrder.purchaseOrder", partnerType),
            })}
          </Text>
        </Flex>
      }
      <Flex justify="space-between" align="flex-start" px="sm">
        <Flex wrap="wrap" styles={flexGrowable}>
          <Flex gap="xs" align="center" h="100%" wrap="wrap" direction="row">
            <Tooltip label={t("common.filterDescription")}>
              <Avatar color="blue" variant="outline">
                <IconFilterPlus />
              </Avatar>
            </Tooltip>
            <Flex direction="column" gap="xs" wrap="wrap">
              <Filter />
              <Sorter />
            </Flex>
          </Flex>
        </Flex>
        <Flex styles={flexStable}>
          <SearchBox />
        </Flex>
      </Flex>
      <Flex w="100%" h="100%" p="sm" justify="space-between" gap="md" direction="column">
        <StandardLayoutTableWrapper>
          <Table withColumnBorders withBorder>
            <PurchaseOrderTableHead pageKey="purchaseOrder" />
            <tbody>
              {(data as AxiosResponse<PurchaseOrdersGet200Response, any>)?.data?.rows?.map(
                (row, idx) => {
                  return (
                    <PurchaseOrderTableRow
                      pageKey="purchaseOrder"
                      key={idx}
                      rowData={row}
                      refetch={refetch}
                      checkedItemIds={checkedMap.get(row.id ?? 0) ?? new Set()}
                      onChangeCheckedItemIds={(updatedSet) => {
                        const childrenCount = row.purchaseOrderItems?.length;

                        setCheckedMap((prev) => {
                          const newMap = new Map(prev);

                          if (childrenCount === 0) {
                            return newMap;
                          }
                          if (updatedSet.size === 0) {
                            newMap.delete(row.id ?? 0);
                          } else {
                            newMap.set(row.id ?? 0, new Set(updatedSet));
                          }
                          return newMap;
                        });
                      }}
                    >
                      {({
                        purchaseOrderItems,
                        isAllChecked,
                        checkedItemIds,
                        handleToggleAll,
                        handleToggleItem,
                      }) => (
                        <tr>
                          <td colSpan={14}>
                            <Table withColumnBorders withBorder>
                              <PurchaseOrderTableInRowHead
                                pageKey="purchaseOrder"
                                key={row.id}
                                isAllChecked={isAllChecked}
                                handleToggleAll={handleToggleAll}
                              />
                              <tbody>
                                {purchaseOrderItems?.length && purchaseOrderItems?.length > 0 ? (
                                  (purchaseOrderItems ?? []).map((item, idex) => (
                                    <PurchaseOrderTableInRowBody
                                      pageKey="purchaseOrderItems"
                                      item={item}
                                      idex={idex}
                                      checkedItemIds={checkedItemIds}
                                      handleToggleItem={handleToggleItem}
                                    />
                                  ))
                                ) : (
                                  <tr>
                                    <td colSpan={7} style={{ textAlign: "center" }}>
                                      {t("common.notFound")}
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                            </Table>
                          </td>
                        </tr>
                      )}
                    </PurchaseOrderTableRow>
                  );
                },
              )}
            </tbody>
          </Table>
        </StandardLayoutTableWrapper>
        <PaginationBox<PurchaseOrdersGet200Response> />
      </Flex>
    </Flex>
  );
};
